import React, { useState } from "react";
import Swal from "sweetalert2";

// --- MOCK DATA ---
const INITIAL_USER = {
  name: "Rahul",
  email: "rahul@example.com",
  phone: "+91 98765 43210",
  location: "Mumbai, India",
  avatar: "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?q=80&w=2080"
};

const ProfileView = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [userData, setUserData] = useState(INITIAL_USER);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);

  // --- HANDLERS ---
  const handleEditToggle = () => {
    if (isEditing) {
      // Save Logic would go here
      Swal.fire({
        icon: 'success',
        title: 'Profile Updated',
        text: 'Your changes have been saved successfully.',
        confirmButtonColor: '#0B1F33',
        timer: 1500,
        showConfirmButton: false
      });
    }
    setIsEditing(!isEditing);
  };

  const handleChange = (e) => {
    setUserData({ ...userData, [e.target.name]: e.target.value });
  };

  const handleLogout = () => {
    Swal.fire({
      title: 'Sign Out?',
      text: "You will be redirected to the homepage.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#0B1F33',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, log out'
    }).then((result) => {
      if (result.isConfirmed) {
        // Redirect logic here
        window.location.href = "/";
      }
    });
  };

  return (
    <div className="animate-fade-in-up pb-12 font-sans text-[#0B1F33]">
      <h2 className="text-2xl font-black mb-6">Profile & Settings</h2>

      {/* 1. HERO PROFILE CARD */}
      <div className="bg-white p-6 md:p-8 rounded-[2rem] border border-gray-100 shadow-sm flex flex-col md:flex-row items-center gap-8 mb-8 relative overflow-hidden group">
        <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-[#0B1F33] to-[#D4AF37]"></div>

        {/* Avatar Section */}
        <div className="relative">
          <div className="w-28 h-28 rounded-full p-1 bg-gradient-to-br from-[#D4AF37] to-transparent">
            <img src={userData.avatar} alt="Profile" className="w-full h-full object-cover rounded-full border-4 border-white shadow-lg" />
          </div>
          <button className="absolute bottom-1 right-1 w-8 h-8 rounded-full bg-[#0B1F33] text-white flex items-center justify-center shadow-md hover:bg-[#D4AF37] hover:text-[#0B1F33] transition-colors border-2 border-white">
            <span className="material-symbols-outlined text-sm">camera_alt</span>
          </button>
        </div>

        {/* User Info (Editable) */}
        <div className="flex-1 w-full text-center md:text-left space-y-2">
          {isEditing ? (
            <div className="grid gap-3">
              <input
                name="name"
                value={userData.name}
                onChange={handleChange}
                className="text-2xl font-black text-[#0B1F33] border-b-2 border-[#D4AF37] outline-none w-full bg-transparent placeholder-gray-300 focus:bg-gray-50 p-1 rounded"
                placeholder="Full Name"
              />
              <input
                name="email"
                value={userData.email}
                onChange={handleChange}
                className="text-sm font-medium text-gray-500 border border-gray-200 rounded px-2 py-1 w-full"
                placeholder="Email Address"
              />
            </div>
          ) : (
            <>
              <h3 className="text-3xl font-black text-[#0B1F33]">{userData.name}</h3>
              <p className="text-sm text-gray-500 font-medium">{userData.email}</p>
              <div className="flex items-center justify-center md:justify-start gap-2">
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] text-[10px] font-bold uppercase tracking-wider rounded-full border border-[#D4AF37]/20">
                  <span className="material-symbols-outlined text-xs">verified</span> Verified Member
                </span>
                <span className="text-xs text-gray-400 font-bold">•</span>
                <span className="text-xs text-gray-400 font-bold">{userData.location}</span>
              </div>
            </>
          )}
        </div>

        {/* Edit Toggle Button */}
        <button
          onClick={handleEditToggle}
          className={`
            px-6 py-2.5 rounded-xl font-bold text-xs uppercase tracking-widest shadow-md transition-all
            ${isEditing ? 'bg-green-500 text-white hover:bg-green-600' : 'bg-[#0B1F33] text-white hover:bg-[#D4AF37] hover:text-[#0B1F33]'}
          `}
        >
          {isEditing ? 'Save Changes' : 'Edit Profile'}
        </button>
      </div>

      {/* 2. SETTINGS GRID */}
      <div className="grid gap-6">

        {/* Account Details */}
        <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
            <h4 className="text-xs font-black text-[#0B1F33] uppercase tracking-[0.2em]">Account</h4>
            <span className="material-symbols-outlined text-gray-400 text-lg">manage_accounts</span>
          </div>

          <div className="divide-y divide-gray-50">
            <div className="p-5 flex items-center justify-between hover:bg-gray-50/80 transition-colors group cursor-pointer">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#E3F2FD] text-[#1565C0] flex items-center justify-center">
                  <span className="material-symbols-outlined">badge</span>
                </div>
                <div>
                  <p className="text-sm font-bold text-[#0B1F33] group-hover:text-[#1565C0] transition-colors">Personal Information</p>
                  <p className="text-xs text-gray-400">{userData.phone}</p>
                </div>
              </div>
              <span className="material-symbols-outlined text-gray-300 group-hover:text-[#1565C0] group-hover:translate-x-1 transition-all">chevron_right</span>
            </div>

            <div className="p-5 flex items-center justify-between hover:bg-gray-50/80 transition-colors group cursor-pointer">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#FFF8E1] text-[#F57F17] flex items-center justify-center">
                  <span className="material-symbols-outlined">shield</span>
                </div>
                <div>
                  <p className="text-sm font-bold text-[#0B1F33] group-hover:text-[#F57F17] transition-colors">Login & Security</p>
                  <p className="text-xs text-gray-400">Password, 2FA</p>
                </div>
              </div>
              <span className="material-symbols-outlined text-gray-300 group-hover:text-[#F57F17] group-hover:translate-x-1 transition-all">chevron_right</span>
            </div>
          </div>
        </div>

        {/* Preferences */}
        <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
            <h4 className="text-xs font-black text-[#0B1F33] uppercase tracking-[0.2em]">Preferences</h4>
            <span className="material-symbols-outlined text-gray-400 text-lg">tune</span>
          </div>

          <div className="divide-y divide-gray-50">
            {/* Notification Toggle */}
            <div className="p-5 flex items-center justify-between hover:bg-gray-50/80 transition-colors">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#E8F5E9] text-[#2E7D32] flex items-center justify-center">
                  <span className="material-symbols-outlined">notifications_active</span>
                </div>
                <div>
                  <p className="text-sm font-bold text-[#0B1F33]">Push Notifications</p>
                  <p className="text-xs text-gray-400">Updates & Reminders</p>
                </div>
              </div>
              <div
                onClick={() => setNotificationsEnabled(!notificationsEnabled)}
                className={`w-12 h-7 rounded-full p-1 cursor-pointer transition-colors duration-300 ${notificationsEnabled ? 'bg-[#D4AF37]' : 'bg-gray-200'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow-sm transform transition-transform duration-300 ${notificationsEnabled ? 'translate-x-5' : 'translate-x-0'}`}></div>
              </div>
            </div>

            <div className="p-5 flex items-center justify-between hover:bg-gray-50/80 transition-colors group cursor-pointer">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#F3E5F5] text-[#7B1FA2] flex items-center justify-center">
                  <span className="material-symbols-outlined">credit_card</span>
                </div>
                <div>
                  <p className="text-sm font-bold text-[#0B1F33] group-hover:text-[#7B1FA2] transition-colors">Payment Methods</p>
                  <p className="text-xs text-gray-400">Manage Cards & UPI</p>
                </div>
              </div>
              <span className="material-symbols-outlined text-gray-300 group-hover:text-[#7B1FA2] group-hover:translate-x-1 transition-all">chevron_right</span>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <button
          onClick={handleLogout}
          className="w-full py-4 rounded-2xl border-2 border-red-50 bg-white text-red-500 font-bold uppercase tracking-widest text-xs hover:bg-red-50 hover:border-red-100 transition-all flex items-center justify-center gap-2 shadow-sm"
        >
          <span className="material-symbols-outlined text-lg">logout</span>
          Sign Out of Eventix
        </button>

      </div>
    </div>
  );
};

export default ProfileView;